import { LoadingSpinner } from '@cue/atoms';
import { useObserver, useQueryParams, useResponsive } from '@cue/hooks';
import { Modal, useModal } from '@cue/organisms';
import { AnalyticsTransformer, Dayjs, TimeUtil } from '@cue/utility';
import { Global } from '@emotion/react';
import styled from '@emotion/styled';
import { useLocation } from '@reach/router';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { GatsbyImage, getImage, withArtDirection } from 'gatsby-plugin-image';
import { IGatsbyImageDataParent } from 'gatsby-plugin-image/dist/src/components/hooks';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

export const BOOTHMAP_TRACKING_PRE = 'boothmap';

export const BoothMap = () => {
  const [trackingBase] = useState<BoothMapTrackingBase>({ sessionId: uuidv4() });
  const [trackingStart, setTrackingStart] = useState<Dayjs>();
  const [queryParams, setQueryParams] = useQueryParams();
  const { i18n } = useTranslation();
  const routerLocation = useLocation();

  const data = useStaticQuery(graphql`
    query getBoothMapPageData {
      boothmapPreviewDesktopBackground: file(
        relativePath: { eq: "assets/IMG_SPS24_BoothMap_Startscreen_03_frei.png" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 1920, placeholder: NONE, formats: [AUTO, PNG])
          fluid(maxWidth: 2000, quality: 100) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      boothmapPreviewMobileBackground: file(
        relativePath: { eq: "assets/booth-map/boothmap-top.png" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 1080, placeholder: NONE, formats: [AUTO, WEBP])
          fluid(maxWidth: 1000, quality: 100) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      openButton: file(relativePath: { eq: "assets/images/open-cursor-2x.png" }) {
        childImageSharp {
          gatsbyImageData(width: 82, placeholder: NONE, formats: [AUTO, WEBP])
        }
      }
      boothmapPreviewForeground: file(
        relativePath: { eq: "assets/booth-map/booth-map-preview-woman.png" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 1920, placeholder: NONE, formats: [AUTO, WEBP])
        }
      }
    }
  `);

  const openButton = data?.openButton && getImage(data.openButton as IGatsbyImageDataParent);

  const [modalRef] = useModal();

  React.useEffect(() => {
    if (queryParams['id']) {
      openModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openModal() {
    if (!queryParams.lang) {
      setQueryParams({ ...queryParams, lang: i18n.language === 'de' ? 'de-DE' : 'en-US' });
    }
    // modalRef.current?.open();
    setIsModalOpen(true);
    setTrackingStart(TimeUtil.getNowUtc());
    AnalyticsTransformer.trackEvent({
      category: BOOTHMAP_TRACKING_PRE,
      type: 'action',
      action: 'open',
      data: {
        ...trackingBase,
      },
    });

    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ste.feature.page({
        id: 'booth-navigator',
        type: 'feature',
        format: 'html5',
        lang: i18n.language,
      });
    } catch (error) {
      console.warn(error);
    }
  }
  function closeModal() {
    // modalRef.current?.open();

    AnalyticsTransformer.trackEvent({
      category: BOOTHMAP_TRACKING_PRE,
      type: 'action',
      action: 'close',
      data: {
        ...trackingBase,
        duration: TimeUtil.getNowUtc().diff(trackingStart) / 1000,
      },
    });

    // modalRef.current?.close();
    navigate(routerLocation.pathname);
    setTimeout(() => {
      location.reload();
    }, 0);
  }

  const [hovering, setHovering] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const btnRef = React.useRef<HTMLDivElement>(null);
  const { width: windowWidth } = useObserver();

  React.useEffect(() => {
    function dragBtn(e: MouseEvent) {
      e.stopPropagation();

      const btn = btnRef.current;
      if (!btn) return;
      if (windowWidth < 1080) {
        btn.style.left = '50%';
        btn.style.top = '50%';
        return;
      }
      const mousePos = { x: e.pageX, y: e.pageY };

      btn.style.left = mousePos.x + 'px';
      btn.style.top = mousePos.y + 'px';
    }
    addEventListener('mousemove', dragBtn);
    return () => removeEventListener('mousemove', dragBtn);
  }, [hovering, windowWidth]);

  const images = React.useMemo(() => {
    const img = getImage(data.boothmapPreviewDesktopBackground);
    if (!img) return null;
    return withArtDirection(img, [
      {
        media: '(max-width: 1024px)',
        image: img,
      },
    ]);
  }, [data]);

  const { isMobile, isMedium} = useResponsive();
  console.log('openButton', openButton);

  return (
    <OuterWrapper onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
      <BoothPreview
        className="cue-project-content-container-element"
        onClick={() => openModal()}
        data-test-id="cue-test-boothmap">
        <BootmapPreviewContainer
          hovering={!isModalOpen && hovering}
          className={!isModalOpen && hovering ? 'over' : 'out'}>
          {images && <GatsbyImage image={images} alt={'Boothmap Preview'} />}
        </BootmapPreviewContainer>
        <BoothmapOpener hovering={!isModalOpen && hovering} ref={btnRef} className="openCursor">
          <GatsbyImage image={openButton} alt={'Open Boothmap'} />
        </BoothmapOpener>
      </BoothPreview>
      <Modal
        show={isModalOpen}
        onVisibilityChange={(isOpen) => setIsModalOpen(isOpen)}
        ref={modalRef}
        onClose={() => {
          closeModal();
        }}
        className="booth-map-modal"
        overrideCSS={{
          '.cue-modal-content': {
            position: 'relative',
            width: '100%',
            height: isMobile || isMedium ? '100%' : 'calc(100%)',
            maxWidth: '100% !important',
            padding: 0,
          },

          '.cue-modal-backdrop': {
            padding: 0,
            '.cue-modal-container': {
              maxWidth: '100%',
              width: isMobile || isMedium ? '100%' : '100%',

              ...(isMobile || isMedium ? { padding: 0 } : {padding: 0}),
            },
          },
          '.cue-modal-close-button-wrapper': {
            '.cue-button-close': {
              background: 'transparent',
              svg: { use: { fill: '#fff' } },
            },
            '.cue-button-close:hover': {
              background: 'transparent',
              svg: { use: { fill: '#00cccc' } },
            },
          },
        }}>
        <BoothMapApp />
      </Modal>
    </OuterWrapper>
  );
};
export interface BoothMapTrackingBase {
  sessionId: string;
}

function BoothMapApp() {
  const {
    i18n: { language },
  } = useTranslation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const langParam = searchParams.get('lang');
    if (language === 'de' && langParam !== 'de-DE') {
      navigate(`${location.pathname}?lang=de-DE`);
    }
    if (language === 'en' && langParam !== 'en-US') {
      navigate(`${location.pathname}?lang=en-US`);
    }

    const scriptId = 'booth-map-script';
    const existingScript = document.getElementById(scriptId);

    // Remove existing script if it's already loaded
    if (existingScript) {
      existingScript.remove();
    }

    // Dynamically add the external script
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = '/assets/booth-map/assets/index-CUEzuBeU.js?' + Math.random();
    script.type = 'module';
    script.crossOrigin = 'anonymous';

    // Add random data attribute to ensure it reinitializes

    document.body.appendChild(script);
    return () => {
      // Clean up the script when the component is unmounted
      if (script) {
        script.remove();
      }
    };
  }, []);

  return (
    <div>
      <Helmet>
        {/* <script defer src="/assets/booth-map/assets/index-BCtMvZJT.js" /> */}
        <link href="/assets/booth-map/assets/index-YKkjL3x5.css" rel="stylesheet" />
      </Helmet>
      <BoothMapContainer id="root-booth-map-standalone" className="booth-map-cue">
        <LoadingSpinner />
      </BoothMapContainer>
      <Global
        //eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        styles={{
          body: {
            overflowY: 'hidden!important',
            '.open-cursor': "cursor: url('/assets/booth-map/open-cursor-2x.png'), auto",
          },
        }}
      />
    </div>
  );
}

const OuterWrapper = styled.div({
  width: '100vw',
  marginLeft: '-5vw',
  [`@media (min-width: 1080px)`]: {
    width: 'auto',
    marginLeft: 0,
  },
});

const BoothmapOpener = styled.div<{ hovering: boolean }>(({ hovering }) => ({
  position: 'absolute',
  zIndex: 1,
  pointerEvents: 'none',
  cursor: 'pointer',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, 0%)',

  [`@media (min-width: 1080px)`]: {
    opacity: hovering ? 1 : 0,
    transform: 'translate(-50%, -70%)',
  },
}));

const BoothPreview = styled.div({
  position: 'relative',

  [`@media (min-width: 1080px)`]: {
    position: 'initial',
  },
});

const BoothMapContainer = styled.div({
  position: 'relative',
  // transform: 'scale(.95)',
  // width: '100%',
  // height: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',

  header: {
    p: {
      '&.logo': {
        display: 'flex',
      },
    },
  },
  '.open-boothmap': {
    opacity: 1,
    position: 'absolute',
    zIndex: 1,
  },
  // '&#root-booth-map-standalone.booth-map-cue': {
  //   height: '95dvh',
  //   '.main-page': {
  //     '--main-width': '95vw',
  //     '--main-height': '95vh',
  //   },
  // },
});

const BootmapPreviewContainer = styled.div<{ hovering: boolean }>({
  position: 'relative',
  // marginBottom: 80,
  cursor: 'pointer',
  // paddingTop: 60,

  ':hover': {
    opacity: 0.7,

    filter: 'drop-shadow(0 0 3.5rem rgba(0,255,185,.5))',
  },
  transition: 'all .4s ease-out',
  '.gatsby-image-wrapper': {
    pointerEvents: 'none',
    height: '100vw',
    width: '100vw',
    left: '50%',

    transform: 'translateX(-50%)',
    [`@media (min-width: 1080px)`]: {
      height: 'auto',
      width: '100%',
    },
  },
});
